<template>
  <div
    class="container-fluid py-4 running-game-page"
    :class="{ 'running-game-dark': isDarkMode }"
  >
    <div class="row">
      <div class="col-lg-7 mb-4">
        <div class="card mb-4">
          <div class="card-header d-flex flex-row align-items-center">
            <argon-button @click="$router.go(-1)" color="primary" size="md">
              <fa-icon icon="fa-solid fa-arrow-left" />
              <span class="ms-2">Zpět</span>
            </argon-button>
            <h5 class="ms-3 mb-0">{{ state.RunningGameDetail.GameName }}</h5>
          </div>
          <div class="card-body">
            <!-- Portfolio table -->
            <div class="card mb-5 section-panel">
              <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
                <div
                  class="d-flex justify-content-between border-radius-lg pt-3 pb-2"
                  :class="isDarkMode ? 'section-head-dark' : 'section-head-light'"
                >
                  <h6 class="ps-3 mb-0">
                    <template v-if="isViewingOtherPlayer && viewedPlayerNick">
                      Pozice - <span :class="playerNameClass">{{ viewedPlayerNick }}</span>
                    </template>
                    <template v-else>Portfolio ve hře</template>
                  </h6>
                  <span v-if="portfolioTotal != null" class="pe-3 mb-0 portfolio-total">
                    {{ store.getters.formatTableNumber(portfolioTotal) }} Kč
                  </span>
                </div>
              </div>
              <div class="card-body px-2 pt-2 pb-0">
                <portfolio-share-cards
                  v-if="showPortfolioTiles"
                  :rows="state.UserGamePosition"
                  :available-capital="availableCapital"
                  @action="getISINTrading"
                  @clear-orders="clearPositionOrders"
                />
                <portfolio-table
                  v-else
                  :rows="state.UserGamePosition"
                />
              </div>
            </div>
            <!-- Assets table -->
            <div v-if="sessionHash && !hideAssetsTable && route.name !== 'Ukončená hra'" class="card mb-5">
              <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
                <div
                  class="d-flex justify-content-between border-radius-lg pt-3 pb-2"
                  :class="isDarkMode ? 'section-head-dark' : 'bg-white shadow-lg'"
                >
                  <h6 class="ps-3 mb-0">Obchodování</h6>
                </div>
              </div>
              <div class="card-body px-2" >
                <assets-table
                  :rows="state.UserGameISINPosition"
                  :available-capital="availableCapital"
                  @action="getISINTrading"
                />
              </div>
            </div>
            <!-- Transactions table -->
            <div class="card section-panel">
              <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
                <div
                  class="d-flex justify-content-between border-radius-lg pt-3 pb-2"
                  :class="isDarkMode ? 'section-head-dark' : 'section-head-light'"
                >
                  <h6 class="ps-3 mb-0">
                    <template v-if="isViewingOtherPlayer && viewedPlayerNick">
                      Transakce virtuálního kapitálu - <span :class="playerNameClass">{{ viewedPlayerNick }}</span>
                    </template>
                    <template v-else>Transakce virtuálního kapitálu ve hře</template>
                  </h6>
                  <label class="d-flex align-items-center gap-2 pe-3 mb-0 interest-toggle">
                    <span class="text-xs text-uppercase font-weight-bolder">Úroky</span>
                    <div class="form-check form-switch mb-0">
                      <input
                        class="form-check-input cursor-pointer"
                        type="checkbox"
                        id="show-interest"
                        v-model="showInterest"
                      />
                    </div>
                  </label>
                </div>
              </div>
              <div class="card-body px-2 pt-2 pb-0">
                <transactions-table :rows="state.UserGameTransaction" :show-interest="showInterest" />
              </div>
            </div>
            <div
              v-if="sessionHash && !isPlayingInThisGame"
              class="spectator-note mt-4 mb-0"
            >
              V této hře nehraješ, můžeš ale, se zpožděním, sledovat obchody a pozice hráčů.
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-5">  
        <div class="card sticky-top">
          <div class="card-header pb-0">
            <div class="d-flex gap-2 w-100 game-tab-btns">
              <argon-button
                class="flex-fill"
                color="primary"
                size="md"
                :variant="!showGameDetail ? 'fill' : 'outline'"
                :active="!showGameDetail"
                @click="showGameDetail = false"
              >
                Průběh hry
              </argon-button>
              <argon-button
                class="flex-fill"
                color="primary"
                size="md"
                :variant="showGameDetail ? 'fill' : 'outline'"
                :active="showGameDetail"
                @click="showGameDetail = true"
              >
                Detail hry
              </argon-button>
            </div>
          </div>
          <div v-if="!showGameDetail" class="card-body">
            <!-- RunningGame chart -->
            <div class="game-chart-bleed">
              <GameChart
                v-if="state.GameChart && (state.GameChart.cfg || state.GameChart.Chart || state.GameChart.Data)"
                :data="state.GameChart"
                :player="viewedPlayerNick || route.query.hrac || ''"
                :own-player="ownPlayerNick"
                :key="`${gameID}-${viewedPlayerNick || route.query.hrac || 'me'}`"
              />
            </div>
            <h6 class="text-capitalize my-4 section-title">Výsledky</h6>
            <div v-for="(result, index) in state.GameResult" :key="index">
              <game-result-table
                :rows="result"
                :searchable="false"
                :highlighted-user-id="viewedUserId"
                :own-user-id="isPlayingInThisGame ? userID : null"
                @result-clicked="changeRunningGameUser"
              />
            </div>
          </div>
          <div v-else class="card-body game-detail-pane">
            <div class="duration-frame mb-3">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <span class="duration-heading">Trvání hry</span>
                <span class="duration-pct">{{ dateDurationPercentage }}&nbsp;%</span>
              </div>
              <div class="progress duration-track">
                <div
                  class="progress-bar duration-bar"
                  role="progressbar"
                  :style="{ width: dateDurationPercentage + '%' }"
                ></div>
              </div>
              <div class="d-flex justify-content-between align-items-center mt-2">
                <span class="duration-label">{{ state.RunningGameDetail.GameFrom }}</span>
                <span class="duration-label">{{ state.RunningGameDetail.GameTo }}</span>
              </div>
            </div>

            <div class="row g-0 mx-0 finance-box mb-3">
              <div
                class="px-3 py-2 text-center finance-col"
                :class="state.RunningGameDetail.GameCredit ? 'col-6 border-end' : 'col-12'"
              >
                <span class="finance-label">Virtuální kapitál</span>
                <span class="finance-value finance-capital">{{ formatGameNumber(state.RunningGameDetail.GameCapital) }}</span>
              </div>
              <div v-if="state.RunningGameDetail.GameCredit" class="col-6 px-3 py-2 text-center">
                <span class="finance-label">Vklad</span>
                <span class="finance-value">{{ formatGameNumber(state.RunningGameDetail.GameCredit) }}</span>
              </div>
            </div>

            <div
              v-if="renderedGameText"
              class="game-desc mb-3"
              v-html="renderedGameText"
            ></div>

            <div v-if="prizeImage || prizeName || renderedPrizeText || sponsor" class="prize-card">
              <h6 class="prize-card-title mb-3">Výhra</h6>
              <IngaPortrait
                v-if="prizeImage"
                class="reward-photo mb-3"
                frame="reward"
                :src="prizeImage"
                :alt="prizeName"
                :sources="prizeSources"
                rotate
                nav
              />
              <div v-if="prizeName" class="prize-name mb-2">{{ prizeName }}</div>
              <div
                v-if="renderedPrizeText"
                class="prize-text mb-3"
                v-html="renderedPrizeText"
              ></div>
              <div v-if="sponsor" class="sponsor-box">
                <span class="finance-label mb-1">Sponzor</span>
                <div class="d-flex align-items-start gap-3">
                  <img
                    v-if="sponsor.SponsorImg"
                    class="sponsor-logo"
                    :src="imgRoot + sponsor.SponsorImg"
                    :alt="sponsor.SponsorName || 'Sponzor'"
                  >
                  <div class="min-w-0">
                    <div v-if="sponsor.SponsorName" class="sponsor-name">{{ sponsor.SponsorName }}</div>
                    <a
                      v-if="sponsor.SponsorURL"
                      class="sponsor-url"
                      :href="sponsor.SponsorURL"
                      target="_blank"
                      rel="noopener noreferrer"
                    >{{ sponsor.SponsorURL }}</a>
                    <div
                      v-if="renderedSponsorText"
                      class="prize-text mt-2 mb-0"
                      v-html="renderedSponsorText"
                    ></div>
                  </div>
                </div>
              </div>
              <div v-if="hasMultipleRewards" class="mt-3">
                <rewards-table :rows="state.RunningGameDetail.Reward" />
              </div>
            </div>
          </div>
        </div> 
      </div>
    </div>
    <base-modal v-if="store.state.modal.isOpen && store.state.modal.view === 'Investment'">
      <investment-card
        :gameID="gameID" 
        :ISINTrading="state.ISINTradingDetail"
        :available-capital="availableCapital"
        :total-capital="gameCapital"
        @reload-data="getRunningGameData"
      />
    </base-modal>
  </div>
</template>

<script setup>
import { ref, reactive, computed, inject, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import dataService from '@/services/dataService';
import PortfolioTable from './components/custom/tables/PortfolioTable.vue';
import PortfolioShareCards from './components/custom/tables/PortfolioShareCards.vue';
import AssetsTable from './components/custom/tables/AssetsTable.vue';
import TransactionsTable from './components/custom/tables/TransactionsTable.vue';
import RewardsTable from './components/custom/tables/RewardsTable.vue';
import GameChart from './components/custom/Charts/GameChart.vue';
import GameResultTable from './components/custom/tables/GameResultTable.vue';
import ArgonButton from '../components/ArgonButton.vue';
import BaseModal from '@/components/BaseModal.vue';
import InvestmentCard from './components/custom/InvestmentCard.vue';
import IngaPortrait from './components/custom/IngaPortrait.vue';
import { rewardImageFiles } from '@/helpers/rewardGallery';
import { formatRichText } from '@/helpers/richText';
import moment from "moment";

//route props
const props = defineProps(['gid', 'pid'])

const store = useStore();
const isDarkMode = computed(() => !!store.state.isDarkMode);
const router = useRouter();
const route = useRoute();
const swal = inject('$swal');
const cookies = inject('$cookies');

const state = reactive({
  UserGamePosition: [],
  UserGameISINPosition: [],
  UserGameTransaction: [],
  GameChart: null,
  GameResult: [],
  RunningGameDetail: [],
  ISINTradingDetail: []
});

const showGameDetail = ref(false);
const hideAssetsTable = ref(true);
const dateDurationPercentage = ref(0);
const showInterest = ref(false);
const viewedUserId = ref(null);

const parseMoney = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const n = Number(String(value).replace(/\u00a0/g, '').replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

const portfolioTotal = computed(() => {
  const rows = Array.isArray(state.UserGamePosition)
    ? state.UserGamePosition
    : Object.values(state.UserGamePosition || {});
  if (!rows.length) return null;
  const sum = rows.reduce((acc, row) => {
    const n = parseMoney(row?.PortfolioValue);
    return acc + (n != null ? n : 0);
  }, 0);
  return sum;
});

const gameCapital = computed(() => {
  const fromGame = parseMoney(state.RunningGameDetail?.GameCapital);
  if (fromGame != null && fromGame > 0) return fromGame;
  return portfolioTotal.value;
});

const isCashRow = (row) => {
  const name = String(row?.Nm || row?.ISINName || "").toLowerCase();
  if (name.includes("nevyužitý") || name.includes("nevyuzity") || name.includes("peníze")) return true;
  const id = row?.ISINID;
  const isin = row?.ISIN;
  const hasId = id != null && String(id).trim() !== "" && Number(id) !== 0;
  const hasIsin = isin != null && String(isin).trim() !== "" && String(isin) !== "0" && isin !== "CZK";
  return !hasId && !hasIsin;
};

const availableCapital = computed(() => {
  const rows = Array.isArray(state.UserGamePosition)
    ? state.UserGamePosition
    : Object.values(state.UserGamePosition || {});
  const cash = rows.find((row) => isCashRow(row));
  if (!cash) return null;
  return parseMoney(cash.PortfolioValue ?? cash.PtfVl ?? cash.Amt);
});

const gameID = computed(() => { return parseInt(props.gid) });
const imgRoot = computed(() => { return  process.env.VUE_APP_IMG_ROOT });
const apiRoot = computed(() => process.env.VUE_APP_API_URL);

const resolveImage = (file) => {
  if (!file) return "";
  const value = String(file);
  if (value.startsWith("http")) return value;
  if (value.startsWith("img/")) return apiRoot.value + value;
  return imgRoot.value + value;
};

const formatGameNumber = (number) => store.getters.formatNumber(number);
const renderedGameText = computed(() => formatRichText(state.RunningGameDetail?.GameText || ""));
const prizeDetail = computed(() => state.RunningGameDetail?.RewardDetail || {});
const prizeName = computed(() => (
  prizeDetail.value.RewardName
  || state.RunningGameDetail?.Reward?.[0]?.RewardName
  || ""
));
const prizeImage = computed(() => resolveImage(
  prizeDetail.value.RewardImg
  || state.RunningGameDetail?.RewardImg
  || state.RunningGameDetail?.Reward?.[0]?.RewardImg
));
const prizeSources = computed(() => {
  const files = rewardImageFiles(
    prizeDetail.value.RewardImg
      || state.RunningGameDetail?.RewardImg
      || state.RunningGameDetail?.Reward?.[0]?.RewardImg,
    prizeDetail.value.RewardImgs
      || state.RunningGameDetail?.RewardImgs
      || state.RunningGameDetail?.Reward?.[0]?.RewardImgs
  );
  return files.map((file) => resolveImage(file)).filter(Boolean);
});
const renderedPrizeText = computed(() => formatRichText(prizeDetail.value.RewardText || ""));
const sponsor = computed(() => {
  const raw = state.RunningGameDetail?.Sponsor;
  return raw && typeof raw === "object" && !Array.isArray(raw) ? raw : null;
});
const renderedSponsorText = computed(() => formatRichText(sponsor.value?.SponsorText || ""));
const hasMultipleRewards = computed(() => {
  const list = state.RunningGameDetail?.Reward;
  return Array.isArray(list) && list.length > 1;
});

const userID = computed(() => store.getters.getUser?.UserID);
const sessionHash = computed(() => store.getters.isLoggedIn);

const toUserId = (value) => {
  const id = Number(value);
  return Number.isFinite(id) ? id : null;
};

const findPlayerByUserId = (id) => {
  const target = toUserId(id);
  const players = state.RunningGameDetail?.Player;
  if (target == null || !Array.isArray(players)) return null;
  return players.find((player) => toUserId(player.i ?? player.UserID) === target) || null;
};

const playerNick = (player) => player?.u || player?.UserNickName;
const playerUserId = (player) => player?.i ?? player?.UserID;
const isOwnUser = (payloadUserID) => toUserId(userID.value) != null && toUserId(userID.value) === toUserId(payloadUserID);
const isPlayingInThisGame = computed(() => !!findPlayerByUserId(userID.value));
const viewedPlayerNick = computed(() => {
  const fromQuery = String(route.query.hrac || "").trim();
  if (fromQuery) return fromQuery;
  return playerNick(findPlayerByUserId(viewedUserId.value)) || "";
});
const isViewingOtherPlayer = computed(() => {
  const viewed = toUserId(viewedUserId.value);
  const me = toUserId(userID.value);
  if (viewed == null) return false;
  if (!sessionHash.value || me == null) return true;
  return viewed !== me;
});
const ownPlayerNick = computed(() => (
  isPlayingInThisGame.value ? (playerNick(findPlayerByUserId(userID.value)) || '') : ''
));
const playerNameClass = computed(() => (
  isPlayingInThisGame.value ? 'player-name-viewed' : 'player-name-accent'
));

const showPortfolioTiles = computed(() => {
  if (route.name === "Ukončená hra") return false;
  if (!sessionHash.value || toUserId(userID.value) == null) return false;
  const viewed = toUserId(viewedUserId.value);
  if (viewed == null) return true;
  return viewed === toUserId(userID.value);
});

const getRunningGameData = async () => {
  hideAssetsTable.value = false;
  try {
    const [userGamePosition, userGameISINPosition, capitalStatement, gameChart] = await Promise.all([
      dataService.GetUserGamePosition(userID.value, sessionHash.value, gameID.value), // Portfolio ve hre
      dataService.GetUserGameISINPosition(userID.value, sessionHash.value, gameID.value), // Obchodovani
      dataService.GetCapitalStatement(userID.value, sessionHash.value, gameID.value), // Transakce
      dataService.GetGameChart(userID.value, sessionHash.value, gameID.value) // Game chart
    ]);

    state.UserGamePosition = Array.isArray(userGamePosition)
      ? userGamePosition
      : (userGamePosition ? [userGamePosition] : []);
    if (userGameISINPosition) state.UserGameISINPosition = userGameISINPosition; 
    if (capitalStatement) state.UserGameTransaction = capitalStatement.TransactionData; 
    if (gameChart) state.GameChart = gameChart;

  } catch (error) {
    console.error('Failed to load running game data', error);
    swal.fire({
      position: 'center',
      icon: 'error',
      text: `Chyba při načítání dat běžící hry`,
      showConfirmButton: false
    });
  }
}

const getRunningGameDetail = async () => {
  try {
    const [gameResult, runningGameDetail] = await Promise.all([
      dataService.GetGameResult(gameID.value),
      dataService.GetRunningGameDetail(gameID.value)
    ]);
  
    state.GameResult = gameResult || [];
    state.RunningGameDetail = runningGameDetail;

  } catch (error) {
    console.error('Failed to get running game detail', error);
    swal.fire({
      position: 'center',
      icon: 'error',
      text: 'Chyba při načítání dat běžící hry',
      showConfirmButton: false
    });
  }
}

const setGameDateDurationPercentage = () => {
  const gameFrom = moment(state.RunningGameDetail?.GameFrom, 'DD.MM.YYYY');
  const gameTo = moment(state.RunningGameDetail?.GameTo, 'DD.MM.YYYY');
  if (!gameFrom.isValid() || !gameTo.isValid()) {
    dateDurationPercentage.value = 0;
    return;
  }
  const nowDate = moment(moment().format('DD.MM.YYYY'), 'DD.MM.YYYY');
  const daysTotal = gameTo.diff(gameFrom, 'days');
  const daysDiff = nowDate.diff(gameFrom, 'days');
  dateDurationPercentage.value = daysTotal > 0 ? Math.round((daysDiff / daysTotal) * 100) : 0;
}

const getISINTrading = async (payload) => {
  if (!isPlayingInThisGame.value) {
    swal.fire({
      position: 'center',
      icon: 'info',
      html: '<h5>V této hře nehraješ, pokyn nejde podat.</h5>',
      showConfirmButton: false,
      timer: 3000,
    });
    return;
  }
  const response = await dataService.GetISINTrading(userID.value, sessionHash.value, gameID.value, payload.ISINID);

  if (response.Result) { 
    state.ISINTradingDetail = response.Data;
    state.ISINTradingDetail.closePosition = !!payload.closePosition;
    state.ISINTradingDetail.manageOrders = !!payload.manageOrders;
    state.ISINTradingDetail.side = payload.side || null;
    const quoteRows = [
      ...(Array.isArray(state.UserGameISINPosition) ? state.UserGameISINPosition : Object.values(state.UserGameISINPosition || {})),
      ...(Array.isArray(state.UserGamePosition) ? state.UserGamePosition : Object.values(state.UserGamePosition || {})),
    ];
    const quote = quoteRows.find((row) => Number(row?.ISINID) === Number(payload.ISINID));
    if (quote) {
      if (state.ISINTradingDetail.PrcDf == null) state.ISINTradingDetail.PrcDf = quote.PrcDf;
      if (state.ISINTradingDetail.PctDf == null) state.ISINTradingDetail.PctDf = quote.PctDf;
      if (state.ISINTradingDetail.ISINLastPrice == null) {
        state.ISINTradingDetail.ISINLastPrice = quote.LPrc ?? quote.ISINLastPrice;
      }
    }
    const title = payload.closePosition
      ? 'Uzavření pozice'
      : payload.manageOrders
        ? 'Inteligentní pokyn'
        : payload.side === 'short'
          ? 'Otevřít SHORT'
          : payload.side === 'long'
            ? 'Otevřít LONG'
            : 'Podání pokynu ve hře';
    store.dispatch('openModal', {
      title,
      view: 'Investment'
    });
  } else { 
    const messages = Array.isArray(response?.ErrorMessage)
      ? response.ErrorMessage.filter(Boolean)
      : [];
    swal.fire({
      position: 'center',
      icon: 'error',
      html: messages.length
        ? messages.map(message => `<h5>${message}</h5>`).join('')
        : '<h5>Pokyn se nepodařilo otevřít.</h5>',
      showConfirmButton: false,
      timer: 3000,
    })
  }
}

const clearPositionOrders = async (payload) => {
  if (!payload?.ISINID) return;
  const types = Array.isArray(payload.types) && payload.types.length ? payload.types : [''];
  try {
    const results = [];
    for (const type of types) {
      results.push(await dataService.ClearPositionOrder(
        userID.value,
        sessionHash.value,
        gameID.value,
        payload.ISINID,
        type
      ));
    }
    const ok = results.every((item) => item?.Result);
    if (ok) await getRunningGameData();
    const messages = [];
    results.forEach((item) => {
      if (item?.Result) {
        messages.push(...(item.InfoMessage || ['Inteligentní pokyn byl smazán']));
      } else {
        messages.push(...(item?.ErrorMessage || ['Pokyn se nepodařilo smazat']));
      }
    });
    swal.fire({
      position: 'center',
      icon: ok ? 'success' : 'error',
      html: messages.map((message) => `<h5>${message}</h5>`).join(''),
      showConfirmButton: false,
      timer: 2500,
    });
  } catch (error) {
    console.error(error);
  }
}

const getUserRunningGameData = async (payloadUserID) => {
  if (!isOwnUser(payloadUserID) || route.name === "Ukončená hra") {
    hideAssetsTable.value = true;
    // load clicked user running game data
    const [userGamePosition, capitalStatement, gameChart] = await Promise.all([
      dataService.GetUserGamePosition(userID.value, sessionHash.value, gameID.value, payloadUserID),
      dataService.GetCapitalStatement(userID.value, sessionHash.value, gameID.value, payloadUserID),
      dataService.GetGameChart(userID.value, sessionHash.value, gameID.value, payloadUserID)
    ]);

    state.UserGamePosition = Array.isArray(userGamePosition)
      ? userGamePosition
      : (userGamePosition ? [userGamePosition] : []);
    state.UserGameTransaction = capitalStatement?.TransactionData || [];
    state.GameChart = gameChart || [];
  } else {
    getRunningGameData();
  }
}

const changeRunningGameUser = (payload) => {
  viewedUserId.value = payload.UserID;
  router.replace({ name: route.name, query: { hrac: payload.UserNickName }, params: { gid: route.params.gid } });
  getUserRunningGameData(payload.UserID);
}

const loadSpectatorView = () => {
  hideAssetsTable.value = true;
  state.UserGameISINPosition = [];
  const raw = state.GameResult;
  let list = raw?.[gameID.value] || raw?.[String(gameID.value)];
  if (!Array.isArray(list) && raw && typeof raw === 'object') {
    const first = Object.values(raw)[0];
    if (Array.isArray(first)) list = first;
  }
  if (!Array.isArray(list)) list = [];
  const nick = route.query.hrac;
  let player = nick
    ? list.find((item) => String(item.UserNickName) === String(nick))
    : null;
  if (!player) {
    player = list.find((item) => item.Rank === 1) || list[0];
  }
  if (!player) {
    viewedUserId.value = null;
    state.UserGamePosition = [];
    state.UserGameTransaction = [];
    return;
  }
  viewedUserId.value = player.UserID;
  if (player.UserNickName && route.query.hrac !== player.UserNickName) {
    router.replace({
      name: route.name,
      query: { hrac: player.UserNickName },
      params: { gid: route.params.gid }
    });
  }
  getUserRunningGameData(player.UserID);
};

const loadOwnRunningGame = () => {
  viewedUserId.value = userID.value;
  if (route.name === "Ukončená hra") {
    getUserRunningGameData(userID.value);
  } else {
    getRunningGameData();
  }
};

const hasGameDetail = () => {
  const detail = state.RunningGameDetail;
  return !!(detail && typeof detail === 'object' && !Array.isArray(detail) && detail.GameName);
};

const loadRunningGame = async () => {
  await getRunningGameDetail();
  if (hasGameDetail()) {
    setGameDateDurationPercentage();
  }
  const cookiePlayer = findPlayerByUserId(cookies.get('userID'));
  const meInGame = findPlayerByUserId(userID.value);

  if (sessionHash.value && meInGame) {
    loadOwnRunningGame();
  } else if (sessionHash.value && !meInGame) {
    loadSpectatorView();
  } else if (route.params.pid) {
    viewedUserId.value = route.params.pid;
    getUserRunningGameData(route.params.pid);
  } else if (cookiePlayer) {
    viewedUserId.value = playerUserId(cookiePlayer);
    router.replace({
      name: route.name,
      query: { hrac: playerNick(cookiePlayer) },
      params: { gid: route.params.gid }
    });
    getUserRunningGameData(playerUserId(cookiePlayer));
  } else {
    loadSpectatorView();
  }
};

watch(sessionHash, (hash, previousHash) => {
  if (hash && hash !== previousHash) {
    loadRunningGame();
  }
});

watch(gameID, () => {
  loadRunningGame();
});

loadRunningGame();
</script>

<style scoped>
.duration-frame {
  padding: 12px 14px 10px;
  border-radius: 0.85rem;
  border: 1px solid #CBD5E1;
  background: #F8FAFC;
}

.running-game-dark .duration-frame {
  background: #1E293B;
  border-color: #334155;
}

.duration-heading,
.finance-label {
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748B;
}

.running-game-dark .duration-heading,
.running-game-dark .finance-label {
  color: #94A3B8;
}

.duration-label {
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748B;
}

.running-game-dark .duration-label {
  color: #94A3B8;
}

.duration-pct {
  font-size: 0.75rem;
  font-weight: 800;
  color: #FF6B21;
}

.running-game-dark .duration-pct {
  color: #FF8A4C;
}

.duration-track {
  height: 10px;
  border-radius: 999px;
  background: #E2E8F0;
  overflow: hidden;
}

.running-game-dark .duration-track {
  background: #0F172A;
}

.duration-bar {
  background: linear-gradient(90deg, #FF7A00 0%, #FF4D00 100%);
  border-radius: 999px;
}

.finance-box {
  border-radius: 0.85rem;
  overflow: hidden;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
}

.running-game-dark .finance-box {
  background: #1E293B;
  border-color: #334155;
}

.running-game-dark .finance-col {
  border-color: #334155 !important;
}

.finance-value {
  display: block;
  margin-top: 0.35rem;
  font-size: 1.15rem;
  font-weight: 800;
  color: #0F172A;
}

.running-game-dark .finance-value {
  color: #E2E8F0;
}

.finance-capital {
  color: #059669 !important;
}

.running-game-dark .finance-capital {
  color: #34D399 !important;
}

.game-desc,
.prize-text {
  font-size: 0.88rem;
  line-height: 1.55;
  color: #334155;
}

.running-game-dark .game-desc,
.running-game-dark .prize-text {
  color: #CBD5E1;
}

.prize-card {
  padding: 14px;
  border-radius: 0.85rem;
  border: 1px solid #E2E8F0;
  background: #FFFFFF;
}

.running-game-dark .prize-card {
  background: #1E293B;
  border-color: #334155;
}

.prize-card-title {
  font-weight: 800;
  letter-spacing: 0.02em;
}

.prize-name {
  font-size: 1.05rem;
  font-weight: 800;
  color: #0F172A;
}

.running-game-dark .prize-name {
  color: #F8FAFC;
}

.sponsor-box {
  margin-top: 4px;
  padding-top: 12px;
  border-top: 1px solid #E2E8F0;
}

.running-game-dark .sponsor-box {
  border-top-color: #334155;
}

.sponsor-logo {
  width: 56px;
  height: 56px;
  object-fit: contain;
  border-radius: 8px;
  background: #F8FAFC;
  flex-shrink: 0;
}

.running-game-dark .sponsor-logo {
  background: #0F172A;
}

.sponsor-name {
  font-weight: 800;
  font-size: 0.95rem;
}

.sponsor-url {
  display: inline-block;
  font-size: 0.78rem;
  font-weight: 600;
  color: #FF6B21;
  word-break: break-all;
}

.running-game-dark .sponsor-url {
  color: #FF8A4C;
}

.reward-photo {
  width: 100%;
}

.player-name-accent {
  color: #FF6B21;
  font-weight: 800;
}

.running-game-dark .player-name-accent {
  color: #FF8A4C;
}

.player-name-viewed {
  color: #5B82AC;
  font-weight: 800;
}

.running-game-dark .player-name-viewed {
  color: #7BA3C9;
}

.spectator-note {
  padding: 8px 4px 0;
  border-radius: 0;
  background: transparent;
  border: none;
  color: #64748b;
  font-size: 0.8rem;
  font-weight: 500;
  line-height: 1.45;
}

.running-game-dark .spectator-note {
  background: transparent;
  border: none;
  color: #94A3B8;
}

.game-chart-bleed {
  margin-left: -0.75rem;
  margin-right: -0.75rem;
}

.running-game-dark {
  min-height: 100vh;
  background: #1E293B !important;
  color: #E2E8F0;
}

.running-game-dark :deep(.card) {
  background: #0F172A !important;
  background-color: #0F172A !important;
  box-shadow: none !important;
  border: 1px solid #334155 !important;
  color: #E2E8F0 !important;
}

.running-game-dark :deep(.card-header),
.running-game-dark :deep(.card-body) {
  background: transparent !important;
  color: #E2E8F0 !important;
}

.running-game-dark :deep(h5),
.running-game-dark :deep(h6),
.running-game-dark :deep(.section-title),
.running-game-dark :deep(.text-dark),
.running-game-dark :deep(.text-navy-blue) {
  color: #E2E8F0 !important;
}

.running-game-dark :deep(.text-primary) {
  color: #FF8A4C !important;
}

.running-game-page :deep(.text-player-viewed) {
  color: #5B82AC !important;
}

.running-game-dark :deep(.text-player-viewed) {
  color: #7BA3C9 !important;
}

.section-panel {
  background: #F1F5F9 !important;
  border: 1px solid #E2E8F0;
  box-shadow: none;
}

.section-head-light {
  background: #E2E8F0;
  border: 1px solid #CBD5E1;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.08);
}

.section-head-light h6 {
  color: #0F172A;
}

.running-game-dark .section-head-dark {
  background: #1E293B !important;
  border: 1px solid #334155;
}

.portfolio-total {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1E293B;
  white-space: nowrap;
}

.running-game-dark .portfolio-total {
  color: #E2E8F0;
}

.game-tab-btns :deep(.btn) {
  flex: 1 1 0;
  min-width: 0;
}

.running-game-dark .game-tab-btns :deep(.btn-outline-primary) {
  color: #E2E8F0;
  border-color: #64748B;
  background: #1E293B;
}

.running-game-dark .game-tab-btns :deep(.btn-outline-primary:hover) {
  color: #fff;
  border-color: #FF8A4C;
  background: #334155;
}

.running-game-dark :deep(.bg-gray-200) {
  background-color: #1E293B !important;
}

.running-game-dark :deep(.vgt-inner-wrap),
.running-game-dark :deep(.vgt-responsive),
.running-game-dark :deep(.vgt-wrap) {
  background: transparent !important;
  box-shadow: none !important;
}

.running-game-dark :deep(.vgt-table) {
  background: transparent !important;
  color: #E2E8F0 !important;
  border: none !important;
}

.running-game-dark :deep(.vgt-table th) {
  background: #1E293B !important;
  color: #94A3B8 !important;
  border-color: #334155 !important;
}

.running-game-dark :deep(.vgt-table td) {
  background: transparent !important;
  color: #E2E8F0 !important;
  border-color: #334155 !important;
}

.running-game-dark :deep(.vgt-table tbody tr:hover),
.running-game-dark :deep(.vgt-table tr.clickable:hover) {
  background-color: #1E293B !important;
}

.running-game-dark :deep(.vgt-wrap__footer) {
  background: #1E293B !important;
  color: #94A3B8 !important;
  border: none !important;
}

.running-game-dark :deep(.footer__row-count__label),
.running-game-dark :deep(.footer__navigation__info),
.running-game-dark :deep(.footer__navigation__page-info),
.running-game-dark :deep(.vgt-wrap__footer .footer__navigation__page-btn) {
  color: #E2E8F0 !important;
}

.running-game-dark :deep(.vgt-table th.sortable button),
.running-game-dark :deep(.vgt-table th.sortable span) {
  color: #94A3B8 !important;
}

.running-game-dark :deep(.vgt-global-search) {
  background: transparent !important;
  border: none !important;
  padding: 0 0 12px !important;
}

.running-game-dark :deep(.vgt-input),
.running-game-dark :deep(.vgt-select) {
  background: #1E293B !important;
  color: #E2E8F0 !important;
  border: 1px solid #334155 !important;
}

.running-game-page :deep(.vgt-row-me),
.running-game-page :deep(.vgt-row-me td) {
  background: rgba(255, 107, 33, 0.12) !important;
}

.running-game-page :deep(.vgt-row-active),
.running-game-page :deep(.vgt-row-active td) {
  background: rgba(91, 130, 172, 0.14) !important;
}

.running-game-dark :deep(.vgt-row-active td) {
  background: rgba(91, 130, 172, 0.22) !important;
}

.interest-toggle .form-switch {
  min-height: 0;
  padding-left: 0;
}

.interest-toggle .form-check-input {
  float: none;
  margin: 0;
  width: 2.4rem;
  height: 1.25rem;
}

.interest-toggle .form-check-input::after {
  top: 50%;
  transform: translate(2px, -50%);
}

.interest-toggle .form-check-input:checked::after {
  transform: translate(21px, -50%);
}

.running-game-dark .interest-toggle {
  color: #94A3B8;
}

.running-game-dark :deep(.form-check-input) {
  background-color: #334155;
  border-color: #475569;
}

.running-game-dark :deep(.form-check-input:checked) {
  background-color: #FF8A4C;
  border-color: #FF8A4C;
}

@media (max-width: 430px) {
  .sticky-top {
    position: static !important;
  }
  .game-tab-btns :deep(.btn) {
    font-size: 0.78rem;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
}
</style>

